import { constantsJson, functionsJson, operatorSymbolsJson, symbolsJson } from "@corca/data";
import { type NodeJson } from "@corca/data";

import { SYMPY_SET_NAME_TO_SYMBOL_KEY } from "../mappings";
import {
  groupNode,
  numberNode,
  matrixNode,
  inlineNode,
  plainTextNode,
  constantNode,
  operatorSymbolNode,
  functionNode,
  labelNode,
  operatorNode,
  variableNode,
} from "../utils";

import { intToNumber, prependMinus, rationalToDecimalString, SympyParseError } from "./shared";

import type { SympyParser } from "./SympyParser";
import type { SemanticExpr, SympyResult } from "../types";
import type { ClosingSymbol, OpeningSymbol } from "@corca/data";

// TODO: refactor these

const piJson = constantsJson.find((c) => c.symbolProps?.key === "symbol-pi")!;
const eulerJson = constantsJson.find((c) => c.symbolProps?.key === "constant-eulers-number")!;
const infinityJson = constantsJson.find((c) => c.symbolProps?.key === "constant-infinity")!;
const goldenRatioJson = constantsJson.find((c) => c.symbolProps?.key === "constant-golden-ratio")!;
const catalanJson = constantsJson.find((c) => c.symbolProps?.key === "constant-catalans-constant")!;
const emptySetJson = constantsJson.find((c) => c.symbolProps?.key === "constant-empty-set")!;
const limitJson = functionsJson.find((f) => f.key === "function-limit")!;
const integralJson = functionsJson.find((f) => f.key === "function-integral")!;
const summationJson = functionsJson.find((f) => f.key === "function-summation")!;
const productJson = functionsJson.find((f) => f.key === "function-product")!;
// const indefiniteIntegralJson = functionsJson.find((f) => f.key === "function-indefinite-integral")!;
const toSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-to")!;
const differentialJson = functionsJson.find((f) => f.key === "function-differential")!;
const commaSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-comma")!;
const unionSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-union")!;
const intersectionSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-intersection")!;
const setDifferenceSymbolJson = operatorSymbolsJson.find(
  (op) => op.key === "symbol-set-difference",
)!;
const notEqualSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-not-equal")!;
const equalSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-equal")!;
const lessThanSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-less-than")!;
const logicalAndSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-logical-and")!;
const logicalOrSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-logical-or")!;
const lessOrEqualSymbolJson = operatorSymbolsJson.find(
  (op) => op.key === "symbol-less-than-or-equal-to",
)!;
const greaterThanSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-greater-than")!;
const greaterOrEqualSymbolJson = operatorSymbolsJson.find(
  (op) => op.key === "symbol-greater-than-or-equal-to",
)!;

// Build a blackboard-bold set symbol (ℝ ℤ ℕ ℚ ℂ) as a variable node, matching
// how the editor represents it, from a SymPy predefined-set operator name.
function setSymbolNodeFromSympyName(sympyName: string): NodeJson[] | null {
  const symbolKey = SYMPY_SET_NAME_TO_SYMBOL_KEY[sympyName];
  if (!symbolKey) return null;
  const entry = symbolsJson.find((s) => s.key === symbolKey);
  if (!entry) return null;
  return [
    {
      type: "variable",
      props: { name: entry.name },
      children: [{ type: "symbol", props: { value: entry.symbol, key: symbolKey }, children: [] }],
    },
  ];
}

export function parseSympyOperator(
  parser: SympyParser,
  sympy: Extract<SympyResult, { type: "operator" }>,
  opts?: { parent?: SympyResult; grouped?: boolean; exponent?: boolean },
): NodeJson[] {
  switch (sympy.name) {
    case "Add": {
      const nodes: NodeJson[] = [];
      const first = parser.parseResult(sympy.args[0]);
      if (first) {
        nodes.push(...first);
      }

      for (let i = 1; i < sympy.args.length; i++) {
        // All args after the first are explicitly signed
        // this way we can write 1 - 2 + 3 instead of 1 + -2 + 3
        const arg = parser.parseResult(sympy.args[i], { signed: true });
        nodes.push(...arg);
      }

      if (opts?.grouped && nodes.length > 1) {
        return [groupNode("(", ")", [matrixNode(1, 1, [inlineNode(...nodes)])])];
      }

      return nodes;
    }

    case "Mul":
      return parser.parseMultiplication(sympy.args);

    case "MatPow":
    case "Pow":
      if (sympy.args.length === 2) {
        return parser.parsePower(sympy.args[0], sympy.args[1]);
      }
      break;

    case "Order": {
      if (sympy.args.length === 0) return [];

      return [
        plainTextNode("O"),
        groupNode("(", ")", [matrixNode(1, 1, [inlineNode(...parser.parseResult(sympy.args[0]))])]),
      ];
    }

    case "Pi":
      return [constantNode(piJson)];

    case "Limit": {
      const [limitArgs, limitVar, limitPoint] = sympy.args;
      return [
        functionNode(
          limitJson,
          [],
          labelNode(
            "right-bottom",
            inlineNode(
              ...parser.parseResult(limitVar),
              operatorSymbolNode(toSymbolJson),
              ...parser.parseResult(limitPoint),
            ),
          ),
          inlineNode(...parser.parseResult(limitArgs)),
        ),
      ];
    }

    case "Integral": {
      const secondArg = sympy.args[1] as Extract<SemanticExpr, { type: "operator" }>;
      const integralVariable = parser.parseResult(secondArg.args[0]);

      const isIndefinite =
        sympy.args.length === 1 ||
        (sympy.args.length === 2 &&
          secondArg.type === "operator" &&
          secondArg.name === "Tuple" &&
          secondArg.args.length === 1);

      const labels: NodeJson[] = isIndefinite
        ? []
        : [
            labelNode("right-bottom", inlineNode(...parser.parseResult(secondArg.args[1]))),
            labelNode("right-top", inlineNode(...parser.parseResult(secondArg.args[2]))),
          ];

      return [
        functionNode(
          integralJson,
          labels,
          inlineNode(
            ...parser.parseResult(sympy.args[0]),
            functionNode(differentialJson, [], inlineNode(...integralVariable)),
          ),
        ),
      ];
    }

    case "Sum":
    case "Product": {
      const funcJson = sympy.name === "Sum" ? summationJson : productJson;
      const body = sympy.args[0];
      const tupleArg = sympy.args[1] as SympyResult;

      // Sum/Product limits come back as a 3-tuple [var, start, end] (sometimes 2 for indefinite).
      // Bottom label: "var = start"; top label: "end".
      const labels: NodeJson[] = [];
      if (Array.isArray(tupleArg) && tupleArg.length >= 2) {
        const tuple = tupleArg as SympyResult[];
        const varArg = tuple[0];
        const startArg = tuple[1];
        const endArg = tuple[2];
        labels.push(
          labelNode(
            "right-bottom",
            inlineNode(
              ...parser.parseResult(varArg),
              operatorSymbolNode(equalSymbolJson),
              ...parser.parseResult(startArg),
            ),
          ),
        );
        if (endArg !== undefined) {
          labels.push(labelNode("right-top", inlineNode(...parser.parseResult(endArg))));
        }
      }

      return [functionNode(funcJson, labels, inlineNode(...parser.parseResult(body)))];
    }

    // Unevaluated derivative (e.g. d/dx f(x) where f is abstract). Render as a fraction:
    //   numerator   = d^N(expr)
    //   denominator = d(x_1)^{n_1} d(x_2)^{n_2} ... where N = sum of orders
    case "Derivative": {
      const exprArg = sympy.args[0];
      const varTuples = sympy.args.slice(1);

      let totalOrder = 0;
      const denomNodes: NodeJson[] = [];
      for (const tuple of varTuples) {
        if (!Array.isArray(tuple) || tuple.length < 2) continue;
        const varArg = tuple[0] as SympyResult;
        const orderArg = tuple[1] as SympyResult;
        const order =
          !Array.isArray(orderArg) && orderArg.type === "number" ? Number(orderArg.value) : 1;
        totalOrder += order;
        const varNodes = parser.parseResult(varArg);
        const dInline = inlineNode(...varNodes);
        denomNodes.push(functionNode(differentialJson, [], dInline));
        if (order > 1) {
          denomNodes.push(operatorNode("power", [inlineNode(numberNode(order.toString()))]));
        }
      }

      const exprNodes = parser.parseResult(exprArg);
      const numeratorDInline = inlineNode(...exprNodes);
      const numeratorD =
        totalOrder <= 1
          ? functionNode(differentialJson, [], numeratorDInline)
          : functionNode(
              differentialJson,
              [],
              operatorNode("power", [inlineNode(numberNode(totalOrder.toString()))]),
              numeratorDInline,
            );

      return [operatorNode("fraction", [inlineNode(numeratorD), inlineNode(...denomNodes)])];
    }

    case "AccumulationBounds": {
      // ignore non-converging results
      return [];
    }

    case "ExprCondPair": {
      const isLastPair =
        opts?.parent && "args" in opts.parent
          ? opts.parent.args.indexOf(sympy) === opts.parent.args.length - 1
          : false;

      if (
        isLastPair &&
        (sympy.args[1].type !== "operator" || sympy.args[1].name !== "BooleanTrue")
      ) {
        parser.warn("Last ExprCondPair is not 'otherwise' (True).");
      }

      return [
        inlineNode(...parser.parseResult(sympy.args[0]), operatorSymbolNode(commaSymbolJson)),
        !isLastPair
          ? inlineNode(...parser.parseResult(sympy.args[1]), operatorSymbolNode(commaSymbolJson))
          : inlineNode({
              type: "plainText",
              props: {
                value: "otherwise.",
              },
              children: [],
            }),
      ];
    }

    case "Unequality":
      return [
        ...parser.parseResult(sympy.args[0]),
        operatorSymbolNode(notEqualSymbolJson),
        ...parser.parseResult(sympy.args[1]),
      ];

    case "Equality":
      return [
        ...parser.parseResult(sympy.args[0]),
        operatorSymbolNode(equalSymbolJson),
        ...parser.parseResult(sympy.args[1]),
      ];

    case "LessThan":
      return [
        ...parser.parseResult(sympy.args[0]),
        operatorSymbolNode(lessOrEqualSymbolJson),
        ...parser.parseResult(sympy.args[1]),
      ];

    case "StrictLessThan":
      return [
        ...parser.parseResult(sympy.args[0]),
        operatorSymbolNode(lessThanSymbolJson),
        ...parser.parseResult(sympy.args[1]),
      ];

    case "GreaterThan":
      return [
        ...parser.parseResult(sympy.args[0]),
        operatorSymbolNode(greaterOrEqualSymbolJson),
        ...parser.parseResult(sympy.args[1]),
      ];

    case "StrictGreaterThan":
      return [
        ...parser.parseResult(sympy.args[0]),
        operatorSymbolNode(greaterThanSymbolJson),
        ...parser.parseResult(sympy.args[1]),
      ];

    case "And": {
      const nodes: NodeJson[] = parser.parseResult(sympy.args[0]);
      for (let i = 1; i < sympy.args.length; i++) {
        nodes.push(operatorSymbolNode(logicalAndSymbolJson));
        nodes.push(...parser.parseResult(sympy.args[i]));
      }
      return nodes;
    }

    case "Or": {
      const nodes: NodeJson[] = parser.parseResult(sympy.args[0]);
      for (let i = 1; i < sympy.args.length; i++) {
        nodes.push(operatorSymbolNode(logicalOrSymbolJson));
        nodes.push(...parser.parseResult(sympy.args[i]));
      }
      return nodes;
    }

    case "Exp1":
      return [constantNode(eulerJson)];

    case "GoldenRatio":
      return [constantNode(goldenRatioJson)];

    case "Catalan":
      return [constantNode(catalanJson)];

    case "EmptySet":
      return [constantNode(emptySetJson)];

    case "FiniteSet": {
      // Render as {a, b, c} to match the multi-solution display.
      const nodes: NodeJson[] = [];
      sympy.args.forEach((arg, i) => {
        if (i > 0) nodes.push(operatorSymbolNode(commaSymbolJson));
        nodes.push(...parser.parseResult(arg));
      });
      return [groupNode("{", "}", [matrixNode(1, 1, [inlineNode(...nodes)])])];
    }

    case "Interval": {
      // Interval(a, b, leftOpen, rightOpen); leftOpen/rightOpen are BooleanTrue/False.
      const left = parser.parseResult(sympy.args[0]);
      const right = parser.parseResult(sympy.args[1]);
      const isLeftOpen = sympy.args[2]?.type === "operator" && sympy.args[2].name === "BooleanTrue";
      const isRightOpen =
        sympy.args[3]?.type === "operator" && sympy.args[3].name === "BooleanTrue";
      const opening: OpeningSymbol = isLeftOpen ? "(" : "[";
      const closing: ClosingSymbol = isRightOpen ? ")" : "]";
      return [
        groupNode(opening, closing, [
          matrixNode(1, 1, [inlineNode(...left, operatorSymbolNode(commaSymbolJson), ...right)]),
        ]),
      ];
    }

    case "Union": {
      const nodes: NodeJson[] = parser.parseResult(sympy.args[0]);
      for (let i = 1; i < sympy.args.length; i++) {
        nodes.push(operatorSymbolNode(unionSymbolJson));
        nodes.push(...parser.parseResult(sympy.args[i]));
      }
      return nodes;
    }

    case "Intersection": {
      const nodes: NodeJson[] = parser.parseResult(sympy.args[0]);
      for (let i = 1; i < sympy.args.length; i++) {
        nodes.push(operatorSymbolNode(intersectionSymbolJson));
        nodes.push(...parser.parseResult(sympy.args[i]));
      }
      return nodes;
    }

    case "Complement": {
      const nodes: NodeJson[] = parser.parseResult(sympy.args[0]);
      for (let i = 1; i < sympy.args.length; i++) {
        nodes.push(operatorSymbolNode(setDifferenceSymbolJson));
        nodes.push(...parser.parseResult(sympy.args[i]));
      }
      return nodes;
    }

    case "Transpose": {
      const transposeData = functionsJson.find((f) => f.key === "function-transpose")!;
      const arg = inlineNode(...parser.parseResult(sympy.args[0]));
      return [functionNode(transposeData, [], arg)];
    }

    case "ComplexInfinity":
      return [constantNode(infinityJson)];

    // Indexed access (from sympy.IndexedBase('a')[i]). Render as a variable with subscript label.
    case "Indexed": {
      const baseName = extractIndexedBaseName(sympy.args[0]);
      const indexArg = sympy.args[1];
      const indexName =
        !Array.isArray(indexArg) && indexArg.type === "symbol" ? indexArg.name : null;
      if (baseName && indexName) {
        return [variableNode(`${baseName}_${indexName}`)];
      }
      return [];
    }

    // Bare IndexedBase without an index (rare; render as plain variable name).
    case "IndexedBase": {
      const baseName = extractIndexedBaseName(sympy);
      return baseName ? [variableNode(baseName)] : [];
    }

    case "BooleanTrue":
      return [plainTextNode("True")];

    case "BooleanFalse":
      return [plainTextNode("False")];

    // --- Natural-form nodes produced by normalizeSemantic (see normalizeSemantic.ts) ---

    case "Neg":
      // -x : a leading minus. In an Add term (signed) the minus is preserved by the signed check.
      return prependMinus(...parser.parseResult(sympy.args[0]));

    case "Frac": {
      // With preferDecimal, a terminating integer/integer rational renders as a decimal. A
      // structural fraction (1/(x+2), Mul/Pow args) has non-number args → intToNumber returns null.
      // opts.exponent keeps x^(3/2) a fraction; sign was lifted into an enclosing Neg.
      if (parser.preferDecimal && !opts?.exponent) {
        const p = intToNumber(sympy.args[0]);
        const q = intToNumber(sympy.args[1]);
        const decimal = p !== null && q !== null ? rationalToDecimalString(p, q) : null;
        if (decimal !== null) return [numberNode(decimal)];
      }
      return [
        operatorNode("fraction", [
          inlineNode(...parser.parseResult(sympy.args[0])),
          inlineNode(...parser.parseResult(sympy.args[1])),
        ]),
      ];
    }

    case "Root": {
      // Root(base, index): index 2 renders as a bare √ (no index shown), otherwise an n-th root.
      const indexArg = sympy.args[1];
      const isSquareRoot =
        !Array.isArray(indexArg) && indexArg.type === "number" && indexArg.value === "2";
      return [
        operatorNode("root", [
          isSquareRoot ? inlineNode() : inlineNode(...parser.parseResult(indexArg)),
          inlineNode(...parser.parseResult(sympy.args[0])),
        ]),
      ];
    }

    case "Reals":
    case "Integers":
    case "Naturals":
    case "Naturals0":
    case "Rationals":
    case "Complexes": {
      const setNodes = setSymbolNodeFromSympyName(sympy.name);
      if (setNodes) return setNodes;
      throw new SympyParseError(`Unknown operator: ${sympy.name}`);
    }

    default:
      throw new SympyParseError(`Unknown operator: ${sympy.name}`);
  }

  return [];
}

/** Extract the base symbol name from an Indexed/IndexedBase argument tree. */
function extractIndexedBaseName(arg: SympyResult): string | null {
  if (Array.isArray(arg)) return null;
  if (arg.type === "symbol") return arg.name;
  if (arg.type === "operator" && arg.name === "IndexedBase") {
    const inner = arg.args[0];
    if (!Array.isArray(inner) && inner.type === "symbol") return inner.name;
  }
  return null;
}
